/**
 * Dados mock do Azim CRM.
 * Shape compatível com o futuro azim-api (REST/OpenAPI).
 * Substitua as funções de lookup por chamadas reais mantendo os mesmos tipos.
 */

import type {
  Owner,
  Stage,
  Account,
  Opportunity,
  Activity,
  InboxItem,
  KPIs,
  TrendItem,
  FunnelItem,
  CurrentUser,
  User,
  Tenant,
  BusinessUnit,
  Partner,
  RoleDefinition,
  AuditEntry,
  AccountTierOption,
} from "../types/domain.js";

// ---- Owners ---------------------------------------------------------------
export const owners: Record<string, Owner> = {
  mil: { id: "mil", name: "Milton Silva Jr.", initials: "MS", color: "#369DA0", role: "CEO" },
  edu: { id: "edu", name: "Eduardo Haas", initials: "EH", color: "#4F84C4", role: "CFO" },
  ana: { id: "ana", name: "Ana Beatriz Rocha", initials: "AR", color: "#C68A2E", role: "Account Exec" },
  raf: { id: "raf", name: "Rafael Nunes", initials: "RN", color: "#2F8F6E", role: "Account Exec" },
  car: { id: "car", name: "Carolina Mendes", initials: "CM", color: "#B5394A", role: "SDR" },
  bru: { id: "bru", name: "Bruno Tavares", initials: "BT", color: "#6B6F9C", role: "Solutions Eng" },
};

// ---- Stages ---------------------------------------------------------------
export const stages: Stage[] = [
  { id: "lead", label: "Lead", color: "var(--stage-lead)" },
  { id: "qualify", label: "Qualificação", color: "var(--stage-qualify)" },
  { id: "propose", label: "Proposta", color: "var(--stage-propose)" },
  { id: "negotiate", label: "Negociação", color: "var(--stage-negotiate)" },
  { id: "won", label: "Fechado · Ganho", color: "var(--stage-won)" },
  { id: "lost", label: "Perdido", color: "var(--stage-lost)" },
];

// ---- Products -------------------------------------------------------------
export const products: string[] = [
  "Gateway Vellus", "Vault Service TSP", "Cloud HSM", "PCI as a Service",
  "Link de Pagamento", "Oversetter", "PoS Custom", "Antifraude", "FlexPerks",
  "EcoReceipt", "VerifyPlus",
];

// ---- Accounts -------------------------------------------------------------
export const accounts: Account[] = [
  { id: "a1", name: "Pernambucanas", domain: "pernambucanas.com.br", segment: "Varejo", tier: "Enterprise", city: "São Paulo · SP", employees: "20.000+", owner: "ana", health: "good", arr: 1480000, openOpps: 2, logoTone: "#B5394A" },
  { id: "a2", name: "Leroy Merlin", domain: "leroymerlin.com.br", segment: "Varejo · Casa", tier: "Enterprise", city: "São Paulo · SP", employees: "12.000+", owner: "raf", health: "good", arr: 960000, openOpps: 1, logoTone: "#2F8F6E", logoText: "L" },
  { id: "a3", name: "Ger7", domain: "ger7.com.br", segment: "Logística", tier: "Mid-Market", city: "Curitiba · PR", employees: "1.200", owner: "ana", health: "watch", arr: 312000, openOpps: 1, logoTone: "#C68A2E" },
  { id: "a4", name: "Drogaria Sttella", domain: "sttella.com.br", segment: "Farmácia", tier: "Mid-Market", city: "Belo Horizonte · MG", employees: "800", owner: "raf", health: "good", arr: 188000, openOpps: 2, logoTone: "#369DA0" },
  { id: "a5", name: "Posto Ipê Rede", domain: "redeipe.com.br", segment: "Combustíveis", tier: "Mid-Market", city: "Goiânia · GO", employees: "2.400", owner: "car", health: "risk", arr: 0, openOpps: 1, logoTone: "#4F84C4" },
  { id: "a6", name: "Móveis Cariri", domain: "moveiscariri.com.br", segment: "Varejo · Móveis", tier: "SMB", city: "Fortaleza · CE", employees: "320", owner: "car", health: "good", arr: 64000, openOpps: 1, logoTone: "#6B6F9C" },
  { id: "a7", name: "Hortifruti Vale", domain: "hortifrutivale.com.br", segment: "Supermercado", tier: "Mid-Market", city: "Vitória · ES", employees: "1.900", owner: "raf", health: "watch", arr: 240000, openOpps: 1, logoTone: "#2F8F6E" },
  { id: "a8", name: "Clínica Aurora", domain: "clinicaaurora.com.br", segment: "Saúde", tier: "SMB", city: "Porto Alegre · RS", employees: "180", owner: "ana", health: "good", arr: 42000, openOpps: 0, logoTone: "#B5394A" },
  { id: "a9", name: "TechPag Brasil", domain: "techpag.com.br", segment: "Fintech", tier: "Mid-Market", city: "São Paulo · SP", employees: "540", owner: "edu", health: "good", arr: 520000, openOpps: 1, logoTone: "#369DA0" },
  { id: "a10", name: "Construfácil", domain: "construfacil.com.br", segment: "Materiais constr.", tier: "Enterprise", city: "Campinas · SP", employees: "6.500", owner: "raf", health: "risk", arr: 710000, openOpps: 1, logoTone: "#C68A2E" },
];

// ---- Account tiers (segmentação configurável por tenant, ADR-0035) --------
// Seed default do backend (epic accounts-list-enrichment, decisão 1):
// Enterprise/Mid-Market/SMB, com `position` ascendente = tier "mais alto" primeiro.
export const accountTiers: AccountTierOption[] = [
  { id: "tier-enterprise", label: "Enterprise", position: 1, active: true },
  { id: "tier-mid-market", label: "Mid-Market", position: 2, active: true },
  { id: "tier-smb", label: "SMB", position: 3, active: true },
];

// ---- Opportunities --------------------------------------------------------
export const opps: Opportunity[] = [
  { id: "o1", name: "Tokenização + Vault Service TSP", account: "a1", stage: "negotiate", value: 540000, prob: 75, owner: "ana", close: "2026-07-10", products: ["Vault Service TSP", "Cloud HSM"], age: 34, nextStep: "Revisar contrato com jurídico", priority: "high" },
  { id: "o2", name: "Gateway + Antifraude — e-commerce", account: "a1", stage: "propose", value: 280000, prob: 55, owner: "ana", close: "2026-08-02", products: ["Gateway Vellus", "Antifraude"], age: 18, nextStep: "Enviar proposta revisada", priority: "high" },
  { id: "o3", name: "PoS Custom — 400 terminais", account: "a2", stage: "negotiate", value: 620000, prob: 70, owner: "raf", close: "2026-06-28", products: ["PoS Custom", "Link de Pagamento"], age: 41, nextStep: "Alinhar rollout logístico", priority: "high" },
  { id: "o4", name: "PCI as a Service — renovação", account: "a3", stage: "qualify", value: 96000, prob: 40, owner: "ana", close: "2026-09-15", products: ["PCI as a Service"], age: 9, nextStep: "Mapear escopo de compliance", priority: "med" },
  { id: "o5", name: "Link de Pagamento + FlexPerks", account: "a4", stage: "propose", value: 74000, prob: 50, owner: "raf", close: "2026-07-22", products: ["Link de Pagamento", "FlexPerks"], age: 21, nextStep: "Demo para o time de marketing", priority: "med" },
  { id: "o6", name: "EcoReceipt — piloto lojas", account: "a4", stage: "lead", value: 38000, prob: 20, owner: "raf", close: "2026-10-01", products: ["EcoReceipt"], age: 4, nextStep: "Agendar call de descoberta", priority: "low" },
  { id: "o7", name: "Oversetter switch On/Off-Us", account: "a5", stage: "qualify", value: 155000, prob: 35, owner: "car", close: "2026-09-05", products: ["Oversetter"], age: 12, nextStep: "Confirmar volume transacional", priority: "med" },
  { id: "o8", name: "VerifyPlus KYC — onboarding", account: "a6", stage: "lead", value: 29000, prob: 20, owner: "car", close: "2026-10-12", products: ["VerifyPlus"], age: 6, nextStep: "Qualificar orçamento", priority: "low" },
  { id: "o9", name: "Gateway Vellus — checkout", account: "a7", stage: "propose", value: 132000, prob: 55, owner: "raf", close: "2026-08-18", products: ["Gateway Vellus"], age: 16, nextStep: "Apresentar à diretoria", priority: "med" },
  { id: "o10", name: "Cloud HSM dedicado", account: "a9", stage: "negotiate", value: 298000, prob: 65, owner: "edu", close: "2026-07-30", products: ["Cloud HSM", "PCI as a Service"], age: 28, nextStep: "Negociar SLA premium", priority: "high" },
  { id: "o11", name: "PoS Custom self-service", account: "a10", stage: "qualify", value: 410000, prob: 30, owner: "raf", close: "2026-09-28", products: ["PoS Custom"], age: 7, nextStep: "Visita técnica às lojas", priority: "high" },
  { id: "o12", name: "Antifraude — camada extra", account: "a3", stage: "propose", value: 88000, prob: 45, owner: "ana", close: "2026-08-09", products: ["Antifraude"], age: 14, nextStep: "Enviar business case", priority: "med" },
  { id: "o13", name: "FlexPerks — programa fidelidade", account: "a2", stage: "lead", value: 120000, prob: 15, owner: "raf", close: "2026-11-04", products: ["FlexPerks"], age: 3, nextStep: "Reunião de descoberta", priority: "low" },
  { id: "o14", name: "Vault Service TSP — expansão", account: "a9", stage: "won", value: 210000, prob: 100, owner: "edu", close: "2026-06-01", products: ["Vault Service TSP"], age: 52, nextStep: "Kickoff de implantação", priority: "med" },
  { id: "o15", name: "Gateway + Link — varejo regional", account: "a7", stage: "won", value: 96000, prob: 100, owner: "raf", close: "2026-05-20", products: ["Gateway Vellus", "Link de Pagamento"], age: 60, nextStep: "Onboarding concluído", priority: "low" },
];

// ---- Activities -----------------------------------------------------------
export const activities: Activity[] = [
  { id: "t1", type: "call", title: "Call de fechamento — Pernambucanas", account: "a1", opp: "o1", owner: "ana", due: "Hoje · 15:00", state: "due", priority: "high" },
  { id: "t2", type: "email", title: "Enviar proposta revisada — Gateway", account: "a1", opp: "o2", owner: "ana", due: "Hoje · 17:30", state: "due", priority: "high" },
  { id: "t3", type: "meeting", title: "Alinhamento rollout PoS — Leroy Merlin", account: "a2", opp: "o3", owner: "raf", due: "Amanhã · 10:00", state: "upcoming", priority: "high" },
  { id: "t4", type: "task", title: "Mapear escopo de compliance — Ger7", account: "a3", opp: "o4", owner: "ana", due: "16 jun", state: "upcoming", priority: "med" },
  { id: "t5", type: "call", title: "Descoberta — Móveis Cariri", account: "a6", opp: "o8", owner: "car", due: "17 jun", state: "upcoming", priority: "low" },
  { id: "t6", type: "email", title: "Follow-up business case — Antifraude", account: "a3", opp: "o12", owner: "ana", due: "Ontem", state: "overdue", priority: "med" },
  { id: "t7", type: "meeting", title: "Visita técnica às lojas — Construfácil", account: "a10", opp: "o11", owner: "raf", due: "18 jun", state: "upcoming", priority: "high" },
  { id: "t8", type: "task", title: "Confirmar volume transacional — Posto Ipê", account: "a5", opp: "o7", owner: "car", due: "Ontem", state: "overdue", priority: "med" },
];

// ---- Inbox ----------------------------------------------------------------
export const inbox: InboxItem[] = [
  { id: "i1", kind: "reply", account: "a1", who: "Fernanda Lima · Pernambucanas", preview: "Recebemos a minuta. Conseguimos fechar ainda este mês se ajustarmos o SLA…", time: "há 12 min", unread: true, owner: "ana" },
  { id: "i2", kind: "ai", account: "a3", who: "Azim AI", preview: "Risco de churn em Ger7: 2 atividades atrasadas e NPS em queda. Sugiro um check-in executivo.", time: "há 40 min", unread: true, owner: "ana" },
  { id: "i3", kind: "meeting", account: "a2", who: "Leroy Merlin", preview: "Reunião de rollout confirmada para amanhã às 10h com o time de operações.", time: "há 1 h", unread: false, owner: "raf" },
  { id: "i4", kind: "reply", account: "a9", who: "Diego Santos · TechPag", preview: "Topamos o SLA premium. Podem emitir o contrato do Cloud HSM dedicado.", time: "há 3 h", unread: false, owner: "edu" },
  { id: "i5", kind: "note", account: "a10", who: "Rafael Nunes", preview: "Diretoria da Construfácil pediu visita técnica antes de avançar com os 400 terminais.", time: "há 5 h", unread: false, owner: "raf" },
  { id: "i6", kind: "won", account: "a9", who: "Sistema", preview: "Oportunidade 'Vault Service TSP — expansão' marcada como Ganha (R$ 210 mil).", time: "ontem", unread: false, owner: "edu" },
];

// ---- KPIs / Forecast ------------------------------------------------------
export const kpis: KPIs = {
  pipelineTotal: 3060000,
  weighted: 1657000,
  wonQuarter: 306000,
  quotaQuarter: 1200000,
  winRate: 0.34,
  avgCycle: 38,
  openCount: 13,
};

export const trend: TrendItem[] = [
  { m: "Jan", won: 140, goal: 180 }, { m: "Fev", won: 165, goal: 180 },
  { m: "Mar", won: 210, goal: 200 }, { m: "Abr", won: 188, goal: 200 },
  { m: "Mai", won: 262, goal: 220 }, { m: "Jun", won: 306, goal: 300 },
];

export const funnel: FunnelItem[] = [
  { stage: "Lead", value: 187, count: 3 },
  { stage: "Qualificação", value: 661, count: 3 },
  { stage: "Proposta", value: 574, count: 4 },
  { stage: "Negociação", value: 1758, count: 4 },
];

// ---- Current User ---------------------------------------------------------
export const currentUser: CurrentUser = {
  id: "u-ana",
  name: "Ana Beatriz Rocha",
  displayName: "Ana Beatriz",
  initials: "AR",
  color: "#C68A2E",
  email: "ana.rocha@vellus.tech",
  phone: "+55 11 98123-4477",
  role: "Account Exec",
  title: "Executiva de Contas · Comercial",
  tenant: "Vellus Brasil",
  timezone: "America/Sao_Paulo",
  twoFactor: true,
  lastPasswordChange: "há 3 meses",
  isMaster: true,
  canSwitchBU: true,
  tenantRole: "Owner Admin",
};

// ---- Users ----------------------------------------------------------------
export const users: User[] = [
  { id: "u-milton", name: "Milton Silva Jr.", initials: "MS", color: "#369DA0", email: "milton@vellus.tech", role: "platform_operator", status: "active", last: "há 8 min", tenant: "Vellus Brasil", seats: 1, mfa: true },
  { id: "u-eduardo", name: "Eduardo Haas", initials: "EH", color: "#4F84C4", email: "eduardo@vellus.tech", role: "tenant_admin", status: "active", last: "há 22 min", tenant: "Vellus Brasil", mfa: true },
  { id: "u-carol", name: "Carolina Mendes", initials: "CM", color: "#B5394A", email: "carolina@vellus.tech", role: "sales_sdr", status: "active", last: "há 1 h", tenant: "Vellus Brasil", mfa: false },
  { id: "u-ana", name: "Ana Beatriz Rocha", initials: "AR", color: "#C68A2E", email: "ana.rocha@vellus.tech", role: "sales_ae", status: "active", last: "agora", tenant: "Vellus Brasil", mfa: true },
  { id: "u-rafa", name: "Rafael Nunes", initials: "RN", color: "#2F8F6E", email: "rafael@vellus.tech", role: "sales_ae", status: "active", last: "há 3 h", tenant: "Vellus Brasil", mfa: true },
  { id: "u-bruno", name: "Bruno Tavares", initials: "BT", color: "#6B6F9C", email: "bruno@vellus.tech", role: "sales_se", status: "active", last: "ontem", tenant: "Vellus Brasil", mfa: false },
];

// ---- Tenants --------------------------------------------------------------
export const tenants: Tenant[] = [
  { id: "t1", name: "Vellus Brasil", slug: "vellus", tz: "America/Sao_Paulo", myRole: "Owner Admin", tone: "#1B1D36", plan: "Enterprise", seatsUsed: 42, seats: 60, status: "active", admin: "Eduardo Haas", mrr: 0, region: "BR" },
  { id: "t2", name: "Frigorífico Sul", slug: "friga-sul", tz: "America/Sao_Paulo", myRole: "TAdmin", tone: "#369DA0", plan: "Growth", seatsUsed: 31, seats: 40, status: "active", admin: "Marcos Vieira", mrr: 21000, region: "BR" },
  { id: "t3", name: "Pernambucanas Pay", slug: "pernamb-pay", tz: "America/Sao_Paulo", myRole: "TAdmin", tone: "#B5394A", plan: "Enterprise", seatsUsed: 88, seats: 120, status: "active", admin: "Fernanda Lima", mrr: 54000, region: "BR" },
  { id: "t4", name: "Vereda Capital", slug: "vereda", tz: "America/Sao_Paulo", myRole: "Viewer", tone: "#6B6F9C", plan: "Starter", seatsUsed: 9, seats: 15, status: "active", admin: "Renata Alves", mrr: 4200, region: "BR" },
  { id: "t5", name: "Polaris Consultoria", slug: "polaris", tz: "America/Sao_Paulo", myRole: "GestorBU", tone: "#4F84C4", plan: "Growth", seatsUsed: 18, seats: 25, status: "trial", admin: "Diego Santos", mrr: 0, region: "BR" },
  { id: "t6", name: "Ger7 Logística", slug: "ger7", tz: "America/Sao_Paulo", myRole: "Viewer", tone: "#C68A2E", plan: "Starter", seatsUsed: 11, seats: 15, status: "past_due", admin: "André Prado", mrr: 4200, region: "BR" },
];

export const currentTenantId = "t1";

// ---- Business Units -------------------------------------------------------
export const bus: BusinessUnit[] = [
  { id: "pag", name: "Pagamentos & Transações", short: "Pagamentos", slug: "bu_pagamentos", tone: "#369DA0", members: 8, accounts: 142, created: "03/02/25", status: "active" },
  { id: "seg", name: "Segurança & Compliance", short: "Segurança", slug: "bu_seguranca", tone: "#4F84C4", members: 5, accounts: 88, created: "14/05/25", status: "active" },
  { id: "pos", name: "POS & Terminais", short: "POS & Terminais", slug: "bu_pos", tone: "#C68A2E", members: 4, accounts: 71, created: "14/05/25", status: "active" },
  { id: "esg", name: "Valor Agregado & ESG", short: "Valor & ESG", slug: "bu_esg", tone: "#2F8F6E", members: 3, accounts: 24, created: "08/01/26", status: "active" },
];

export const currentBUId = "pag";

// ---- Partners -------------------------------------------------------------
export const partners: Partner[] = [
  { id: "p1", name: "Polaris Consultoria", initials: "PC", tone: "#4F84C4", role: "Implementador", since: 2023, status: "active", mrr: 12, setup: 20, hw: 6, opps: 11, ytd: 47220, contacts: 3, slug: "prtn_pc03" },
  { id: "p2", name: "Núcleo Canal", initials: "NC", tone: "#369DA0", role: "Revenda", since: 2024, status: "active", mrr: 18, setup: 25, hw: 10, opps: 7, ytd: 32180, contacts: 2, slug: "prtn_nc07" },
];

// ---- Roles ----------------------------------------------------------------
// Mapa de definições visuais por papel canônico (ADR-0015).
// Rótulos de exibição pt-BR espelham ROLE_LABELS de domain.ts.
export const roles: Record<string, RoleDefinition> = {
  platform_operator: { id: "platform_operator", label: "Operador de Plataforma", tone: "danger", desc: "Acesso global a todos os tenants" },
  tenant_admin: { id: "tenant_admin", label: "Administrador do Tenant", tone: "accent", desc: "Administra a organização" },
  bu_manager: { id: "bu_manager", label: "Gestor de BU", tone: "info", desc: "Gestão de time e pipeline da BU" },
  sales_ae: { id: "sales_ae", label: "Account Executive", tone: "neutral", desc: "Executivo de contas" },
  sales_sdr: { id: "sales_sdr", label: "SDR", tone: "neutral", desc: "Pré-vendas" },
  sales_se: { id: "sales_se", label: "Sales Engineer", tone: "neutral", desc: "Engenharia de soluções pré-venda" },
  viewer: { id: "viewer", label: "Visualizador", tone: "neutral", desc: "Somente leitura" },
};

// ---- Conversations --------------------------------------------------------
// Shape espelha ConversationSummaryDto e MessageDto do Conversation.Contracts.

import type { ConversationSummary, Message } from "../features/conversations/types.js";

export const conversations: ConversationSummary[] = [
  {
    id: "conv-1",
    tenantId: "t1",
    status: "Open",
    unreadCount: 3,
    lastMessageAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    lastMessagePreview: "Recebemos a minuta. Conseguimos fechar ainda este mês?",
    assignedUserId: "u-ana",
    contact: {
      accountId: "a1",
      displayName: "Fernanda Lima",
      channelIdentity: "+55 11 99801-2345",
    },
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
  },
  {
    id: "conv-2",
    tenantId: "t1",
    status: "Pending",
    unreadCount: 1,
    lastMessageAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    lastMessagePreview: "Preciso de suporte com a integração do Gateway.",
    contact: {
      displayName: "Diego Santos",
      channelIdentity: "+55 21 98765-4321",
    },
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
  },
  {
    id: "conv-3",
    tenantId: "t1",
    status: "Open",
    unreadCount: 0,
    lastMessageAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    lastMessagePreview: "Reunião de rollout confirmada para amanhã.",
    assignedUserId: "u-rafa",
    contact: {
      accountId: "a2",
      displayName: "Leroy Merlin · Operações",
      channelIdentity: "+55 11 3300-1010",
    },
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "conv-4",
    tenantId: "t1",
    status: "Closed",
    unreadCount: 0,
    lastMessageAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    lastMessagePreview: "Contrato assinado. Obrigado!",
    assignedUserId: "u-eduardo",
    contact: {
      accountId: "a9",
      displayName: "Diego Santos · TechPag",
      channelIdentity: "+55 11 91234-5678",
    },
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

export const conversationMessages: Record<string, Message[]> = {
  "conv-1": [
    {
      id: "msg-1-1",
      conversationId: "conv-1",
      direction: "In",
      body: "Olá! Recebi a proposta de Vault Service TSP. Tenho algumas dúvidas sobre o SLA.",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-1-2",
      conversationId: "conv-1",
      direction: "Out",
      body: "Boa tarde, Fernanda! Com prazer. O SLA padrão é de 99,9% de uptime com suporte 24/7. Posso detalhar os níveis de criticidade?",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 47 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-1-3",
      conversationId: "conv-1",
      direction: "In",
      body: "Sim, por favor! E também quero entender o processo de migração.",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-1-4",
      conversationId: "conv-1",
      direction: "Out",
      body: "Claro. Enviei a minuta do contrato com os SLAs detalhados. Podemos revisar juntos em uma call?",
      ackStatus: "Delivered",
      sentAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-1-5",
      conversationId: "conv-1",
      direction: "In",
      body: "Recebemos a minuta. Conseguimos fechar ainda este mês?",
      ackStatus: "Delivered",
      sentAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    },
  ],
  "conv-2": [
    {
      id: "msg-2-1",
      conversationId: "conv-2",
      direction: "In",
      body: "Preciso de suporte com a integração do Gateway.",
      ackStatus: "Delivered",
      sentAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    },
  ],
  "conv-3": [
    {
      id: "msg-3-1",
      conversationId: "conv-3",
      direction: "In",
      body: "Confirmando: reunião de rollout amanhã às 10h com o time de operações.",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    },
  ],
  "conv-4": [
    {
      id: "msg-4-1",
      conversationId: "conv-4",
      direction: "In",
      body: "Topamos o SLA premium. Podem emitir o contrato do Cloud HSM dedicado.",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-4-2",
      conversationId: "conv-4",
      direction: "Out",
      body: "Ótimo! Contrato emitido. Assim que assinado, iniciamos o onboarding.",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 2.5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "msg-4-3",
      conversationId: "conv-4",
      direction: "In",
      body: "Contrato assinado. Obrigado!",
      ackStatus: "Read",
      sentAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
};

// ---- Audit ----------------------------------------------------------------
export const audit: AuditEntry[] = [
  { id: "l1", actor: "Eduardo Haas", role: "tenant_admin", action: "user.password_reset", target: "Lucas Ferreira", sev: "high", ip: "189.40.22.7", time: "Hoje · 14:32", cat: "Segurança" },
  { id: "l2", actor: "Ana Beatriz Rocha", role: "sales_ae", action: "opportunity.stage_changed", target: "Tokenização + Vault TSP", sev: "info", ip: "177.92.10.3", time: "Hoje · 13:58", cat: "Pipeline" },
];

// ---- Lookup helpers -------------------------------------------------------
export function tenantById(id: string): Tenant | undefined {
  return tenants.find((t) => t.id === id);
}

export function buById(id: string): BusinessUnit | undefined {
  return bus.find((b) => b.id === id);
}

export function accountById(id: string): Account | undefined {
  return accounts.find((a) => a.id === id);
}

export function oppsByAccount(id: string): Opportunity[] {
  return opps.filter((o) => o.account === id);
}

// ---- Formatters -----------------------------------------------------------
export function fmtBRL(n: number): string {
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)} mi`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)} mil`;
  return `R$ ${n}`;
}

export function fmtFull(n: number): string {
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

// ---- Call Recordings (mock para área "recordings") -------------------------

export interface MockRecordingItem {
  id: string;
  /** Nome canônico no formato `yyyy.MM.dd.{id}`. */
  name: string;
  tenantId: string;
  subjectType: string;
  subjectId: string;
  status: "pending" | "processing" | "transcribed" | "failed" | "no_speech";
  storageUri?: string;
  consentReference: string;
  language?: string;
  noSpeech?: boolean;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MockTranscriptSegment {
  sequence: number;
  speaker: string;
  start: number;
  end: number;
  text: string;
}

export interface MockTranscriptResponse {
  recordingId: string;
  /** Nome canônico no formato `yyyy.MM.dd.{recordingId}`. */
  name: string;
  status: string;
  language?: string;
  noSpeech?: boolean;
  segments: MockTranscriptSegment[];
  audioUrl?: string;
  audioUrlExpiresAt?: string;
}

export interface MockContextChunk {
  recordingId: string;
  chunkText: string;
  score: number;
}

export const recordings: MockRecordingItem[] = [
  {
    id: "rec-1",
    name: "2026.06.25.rec-1",
    tenantId: "t1",
    subjectType: "opportunity",
    subjectId: "o1",
    status: "transcribed",
    storageUri: "gs://azim-recordings/t1/rec-1.opus",
    consentReference: "LGPD-CONSENT-2026-001",
    language: "pt",
    noSpeech: false,
    createdAt: "2026-06-25T14:30:00Z",
    updatedAt: "2026-06-25T14:35:00Z",
  },
  {
    id: "rec-2",
    name: "2026.06.26.rec-2",
    tenantId: "t1",
    subjectType: "opportunity",
    subjectId: "o1",
    status: "no_speech",
    storageUri: "gs://azim-recordings/t1/rec-2.opus",
    consentReference: "LGPD-CONSENT-2026-002",
    noSpeech: true,
    createdAt: "2026-06-26T09:00:00Z",
    updatedAt: "2026-06-26T09:02:00Z",
  },
  {
    id: "rec-3",
    name: "2026.06.27.rec-3",
    tenantId: "t1",
    subjectType: "partner",
    subjectId: "p1",
    status: "transcribed",
    storageUri: "gs://azim-recordings/t1/rec-3.opus",
    consentReference: "LGPD-CONSENT-2026-003",
    language: "pt",
    noSpeech: false,
    createdAt: "2026-06-27T10:00:00Z",
    updatedAt: "2026-06-27T10:06:00Z",
  },
];

export const transcripts: Record<string, MockTranscriptResponse> = {
  "rec-1": {
    recordingId: "rec-1",
    name: "2026.06.25.rec-1",
    status: "transcribed",
    language: "pt",
    noSpeech: false,
    segments: [
      { sequence: 0, speaker: "SPEAKER_00", start: 0.0, end: 4.2, text: "Olá, vamos revisar o status da oportunidade de Vault Service TSP." },
      { sequence: 1, speaker: "SPEAKER_01", start: 4.5, end: 9.1, text: "Certo. Estamos na fase de negociação, contrato quase fechado." },
      { sequence: 2, speaker: "SPEAKER_00", start: 9.3, end: 13.8, text: "Ótimo. Precisamos alinhar o SLA premium antes do kickoff." },
      { sequence: 3, speaker: "SPEAKER_01", start: 14.0, end: 18.5, text: "Concordo. Vou enviar a minuta revisada ainda hoje." },
    ],
    audioUrl: "https://storage.example.com/signed/rec-1.opus?token=abc123",
    audioUrlExpiresAt: "2026-07-01T00:00:00Z",
  },
  "rec-3": {
    recordingId: "rec-3",
    name: "2026.06.27.rec-3",
    status: "transcribed",
    language: "pt",
    noSpeech: false,
    segments: [
      { sequence: 0, speaker: "SPEAKER_00", start: 0.0, end: 3.5, text: "Bem-vindo à reunião de parceria com a Polaris Consultoria." },
      { sequence: 1, speaker: "SPEAKER_01", start: 3.8, end: 8.0, text: "Obrigado. Vamos discutir a expansão do canal de implementação." },
    ],
  },
};

export const contextChunks: Record<string, MockContextChunk[]> = {
  "opportunity:o1": [
    { recordingId: "rec-1", chunkText: "SLA premium a ser alinhado antes do kickoff com o cliente.", score: 0.94 },
    { recordingId: "rec-1", chunkText: "Minuta revisada a ser enviada ainda hoje pelo executivo.", score: 0.88 },
  ],
  "partner:p1": [
    { recordingId: "rec-3", chunkText: "Expansão do canal de implementação discutida com a Polaris.", score: 0.91 },
  ],
};

// ---- Pending Recordings (mock para GET /v1/recordings/pending) ---------------

/** Shape de uma gravação pendente de classificação no mock. */
export interface MockPendingRecordingItem {
  id: string;
  /** Nome canônico no formato cloud. */
  name: string;
  /** Data de criação (ISO 8601 UTC). */
  createdAt: string;
  /** Duração em segundos — opcional. */
  durationSeconds?: number;
  status: "awaiting_classification";
  /** URL assinada fictícia para reprodução de áudio no mock (simula presigned GET). */
  audioUrl?: string;
  /** Data de expiração da URL assinada (ISO 8601 UTC). */
  audioUrlExpiresAt?: string;
}

/** Gravações sincronizadas pelo widget que ainda não têm subject vinculado. */
export const pendingRecordings: MockPendingRecordingItem[] = [
  {
    id: "rec-p1",
    name: "2026.07.01.rec-p1",
    createdAt: "2026-07-01T10:30:00Z",
    durationSeconds: 423,
    status: "awaiting_classification",
    audioUrl: "https://storage.example.com/mock/rec-p1.mp4?mock-signature=1",
    audioUrlExpiresAt: "2026-07-01T10:35:00Z",
  },
  {
    id: "rec-p2",
    name: "2026.07.01.rec-p2",
    createdAt: "2026-07-01T14:00:00Z",
    durationSeconds: 180,
    status: "awaiting_classification",
    audioUrl: "https://storage.example.com/mock/rec-p2.mp4?mock-signature=2",
    audioUrlExpiresAt: "2026-07-01T14:05:00Z",
  },
  {
    id: "rec-p3",
    name: "2026.07.02.rec-p3",
    createdAt: "2026-07-02T09:00:00Z",
    status: "awaiting_classification",
    // rec-p3 não tem audioUrl → botão de play deve ficar desabilitado no mock
  },
];

// ---------------------------------------------------------------------------
// Distribuição do Azim Recorder (cliente desktop)
// ---------------------------------------------------------------------------

/** Release fictícia do Azim Recorder para uma plataforma (dev/preview). */
interface MockDesktopClientRelease {
  version: string;
  sizeBytes: number;
  sha256: string;
  releasedAt: string;
  downloadUrl: string;
  expiresAt: string;
}

/**
 * Releases do Azim Recorder por plataforma (slug → release). Chave ausente =
 * nenhuma release publicada para aquela plataforma, simulando o 404 do backend
 * real — por isso "macos-x64" fica de fora: exercita o estado vazio honesto da
 * tela de download em dev, sem precisar de flag extra.
 *
 * Duas plataformas presentes e uma ausente é proposital: em dev dá para ver a
 * tela cheia nos dois sistemas e o aviso de "ainda não há versão publicada" sem
 * trocar nada. Vale lembrar que a release Windows aqui é FICTÍCIA — no ambiente
 * real o manifesto ainda não tem entrada Windows, porque o empacotador do MSI
 * não existe, e o que o visitante vê lá é justamente o estado vazio.
 */
export const desktopClientReleases: Record<string, MockDesktopClientRelease> = {
  "macos-arm64": {
    version: "1.2.0",
    sizeBytes: 47_662_080,
    sha256: "a3f1c9e7b8d2456f0e1a2b3c4d5e6f7089abcdef0123456789abcdef0123456",
    releasedAt: "2026-08-01T13:00:00Z",
    downloadUrl:
      "https://storage.example.com/mock/desktop-client/AzimRecorder-1.2.0-arm64.dmg?mock-signature=1",
    expiresAt: "2099-01-01T00:00:00Z",
  },
  "windows-x64": {
    version: "1.2.0",
    sizeBytes: 62_914_560,
    sha256: "b7e2d4a1c0f3e5b8d9a6c4f2e0b1d3a5c7e9f0b2d4a6c8e0f1b3d5a7c9e1f0b2",
    releasedAt: "2026-08-01T13:00:00Z",
    downloadUrl:
      "https://storage.example.com/mock/desktop-client/AzimRecorder-1.2.0-x64.msi?mock-signature=1",
    expiresAt: "2099-01-01T00:00:00Z",
  },
};
